import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';

const VACIO = {
  id: null, fecha: '', maquina_fiscal: '', reporte_z: '', factura_inicial: '', factura_final: '',
  nc_inicial: '', nc_final: '', total: '', total_exento_ventas: '', base_imponible_ventas: '',
  alicuota_ventas: '16', iva_ventas: '', iva_retenido: '', nro_comprobante: '',
  base_igtf: '', alicuota_igtf: '', igtf_percibido: ''
};

export default function CierreFiscalDiario() {
  const { empresaActiva, rolActual } = useAuth();
  // El operador carga cierres; editarlos y eliminarlos es de contador o más
  // (lo exige el servidor), así que a él no se le muestran esos íconos.
  const puedeCorregir = ['contador', 'gerente', 'admin'].includes(rolActual);
  const [cierres, setCierres] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');

  function cargar() {
    if (!empresaActiva) return;
    setCargando(true);
    api.get('/venta-pos-diaria')
      .then((res) => setCierres(res.data.datos))
      .finally(() => setCargando(false));
  }
  useEffect(cargar, [empresaActiva]);

  function abrirNuevo() {
    setError('');
    setForm({ ...VACIO, fecha: hoyCaracas() });
  }
  function abrirEditar(c) {
    setError('');
    setForm({ ...c, fecha: String(c.fecha).slice(0, 10) });
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    try {
      if (form.id) await api.put(`/venta-pos-diaria/${form.id}`, form);
      else await api.post('/venta-pos-diaria', form);
      setForm(null);
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar el cierre');
    }
  }

  async function eliminar(c) {
    if (!window.confirm(`¿Eliminar el cierre de "${c.maquina_fiscal}" del ${formatFecha(c.fecha)}?`)) return;
    try {
      await api.delete(`/venta-pos-diaria/${c.id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar');
    }
  }

  // Todos los cierres cargados, con cada campo del reporte Z (montos en Bs).
  function contenidoExcel() {
    const n = (v) => (v === null || v === '' || v === undefined ? null : Number(v));
    return {
      titulo: 'Cierre fiscal diario',
      detalle: 'Reportes Z de la máquina fiscal · montos en Bs',
      hojas: [{
        nombre: 'Cierres Z',
        columnas: [
          { titulo: 'Fecha', tipo: 'fecha' }, { titulo: 'Máquina' }, { titulo: 'Reporte Z' },
          { titulo: 'Factura inicial' }, { titulo: 'Factura final' }, { titulo: 'NC inicial' }, { titulo: 'NC final' },
          { titulo: 'Total', tipo: 'monto' }, { titulo: 'Exento', tipo: 'monto' }, { titulo: 'Base imponible', tipo: 'monto' },
          { titulo: 'Alícuota %', tipo: 'monto' }, { titulo: 'IVA', tipo: 'monto' }, { titulo: 'IVA retenido', tipo: 'monto' },
          { titulo: 'N° comprobante' }, { titulo: 'Base IGTF', tipo: 'monto' }, { titulo: 'Alícuota IGTF %', tipo: 'monto' }, { titulo: 'IGTF percibido', tipo: 'monto' }
        ],
        filas: cierres.map((c) => [
          String(c.fecha).slice(0, 10), c.maquina_fiscal, c.reporte_z || '', c.factura_inicial || '', c.factura_final || '',
          c.nc_inicial || '', c.nc_final || '', n(c.total), n(c.total_exento_ventas), n(c.base_imponible_ventas),
          n(c.alicuota_ventas), n(c.iva_ventas), n(c.iva_retenido), c.nro_comprobante || '',
          n(c.base_igtf), n(c.alicuota_igtf), n(c.igtf_percibido)
        ])
      }]
    };
  }

  return (
    <div className="space-y-6">
      <EncabezadoImpresion titulo="Cierre fiscal diario" detalle="Reportes Z de la máquina fiscal · montos en Bs" />
      <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Cierre fiscal diario<BotonAyuda /></h1>
          <p className="text-sm text-slate-500">
            Cierre Z de la máquina fiscal (restaurantes) — alimenta el Libro de Ventas (#34). No reemplaza el módulo de Caja/POS (#91).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Exportar excel={contenidoExcel} disabled={cierres.length === 0} />
          <button onClick={abrirNuevo} title="Nuevo cierre" className="bg-primary-600 text-white p-2 rounded-md hover:bg-primary-700">
            <Plus size={18} />
          </button>
        </div>
      </div>

      {cargando ? (
        <p className="text-slate-500">Cargando...</p>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="text-left p-2">Fecha</th>
                <th className="text-left p-2">Máquina</th>
                <th className="text-left p-2">Reporte Z</th>
                <th className="text-left p-2">Facturas</th>
                <th className="text-right p-2">Total</th>
                <th className="text-right p-2">IVA</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {cierres.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-slate-400 p-6">Todavía no hay cierres cargados.</td></tr>
              ) : cierres.map((c) => (
                <tr key={c.id}>
                  <td className="p-2">{formatFecha(c.fecha)}</td>
                  <td className="p-2">{c.maquina_fiscal}</td>
                  <td className="p-2">{c.reporte_z || '—'}</td>
                  <td className="p-2 text-slate-500">{c.factura_inicial || '—'} – {c.factura_final || '—'}</td>
                  <td className="p-2 text-right">{formatMonto(c.total)}</td>
                  <td className="p-2 text-right">{formatMonto(c.iva_ventas)}</td>
                  <td className="p-2">
                    <div className="flex items-center justify-end gap-2 text-slate-400 print:hidden">
                      {puedeCorregir && (
                        <>
                          <button onClick={() => abrirEditar(c)} className="hover:text-slate-700" title="Editar"><Pencil size={15} /></button>
                          <button onClick={() => eliminar(c)} className="hover:text-red-600" title="Eliminar"><Trash2 size={15} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <Modal title={form.id ? 'Editar cierre fiscal' : 'Nuevo cierre fiscal diario'} onClose={() => setForm(null)}>
          <form onSubmit={guardar} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Fecha</label>
                <input type="date" required value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Máquina fiscal</label>
                <input required value={form.maquina_fiscal} onChange={(e) => setForm({ ...form, maquina_fiscal: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Reporte Z</label>
                <input value={form.reporte_z} onChange={(e) => setForm({ ...form, reporte_z: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Factura inicial</label>
                <input value={form.factura_inicial} onChange={(e) => setForm({ ...form, factura_inicial: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Factura final</label>
                <input value={form.factura_final} onChange={(e) => setForm({ ...form, factura_final: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">NC inicial</label>
                <input value={form.nc_inicial} onChange={(e) => setForm({ ...form, nc_inicial: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">NC final</label>
                <input value={form.nc_final} onChange={(e) => setForm({ ...form, nc_final: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Total del día</label>
                <input type="number" step="0.01" required value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Total exento</label>
                <input type="number" step="0.01" value={form.total_exento_ventas} onChange={(e) => setForm({ ...form, total_exento_ventas: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Base imponible</label>
                <input type="number" step="0.01" value={form.base_imponible_ventas} onChange={(e) => setForm({ ...form, base_imponible_ventas: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Alícuota IVA %</label>
                <input type="number" step="0.01" value={form.alicuota_ventas} onChange={(e) => setForm({ ...form, alicuota_ventas: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">IVA</label>
                <input type="number" step="0.01" value={form.iva_ventas} onChange={(e) => setForm({ ...form, iva_ventas: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">IVA retenido</label>
                <input type="number" step="0.01" value={form.iva_retenido} onChange={(e) => setForm({ ...form, iva_retenido: e.target.value })} className="border rounded-md px-2 py-1.5 w-full text-sm" />
              </div>
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setForm(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
              <button type="submit" className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700">Guardar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
